// Onboarding.jsx — first run.
//
// Before this, a new user signed in and landed on Today with an empty pantry,
// no API key, no ZIP, and no coffee gear, with nothing indicating where to
// start. Half the app silently does nothing without a key.
//
// Design rules:
//   - Only two steps are REQUIRED (provider key, coffee gear). Everything else
//     is skippable, and says so. A setup wizard that demands twelve answers
//     before showing the app is worse than no wizard.
//   - Every step writes as you go rather than batching at the end, so quitting
//     halfway leaves real settings behind instead of nothing.
//   - Nothing here is Josh-specific: the coffee gear list comes from
//     APPLIANCE_LIBRARY, and the household step is blank by default.

import { useState } from "react";
import { APPLIANCE_LIBRARY } from "../lib/appliances";
import { PROVIDERS, providerSupports } from "../lib/llm.js";
import { setStoredZip } from "../lib/weather";
import { TASTE_PROFILES, coffeeRulesFor, gearLine } from "../lib/coffee-taste.js";

const COFFEE_GEAR = APPLIANCE_LIBRARY.filter(a => a.category === "coffee");

// Brewers first, then grinders — people think "what do I brew with" before
// "what do I grind with".
const BREWERS = COFFEE_GEAR.filter(a => !a.capabilities.includes("burr_grind"));
const GRINDERS = COFFEE_GEAR.filter(a => a.capabilities.includes("burr_grind"));

const STEPS = ["welcome", "provider", "coffee", "taste", "home", "done"];

export function Onboarding({ store, onFinish }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [keyDraft, setKeyDraft] = useState(store.apiKey || "");
  const [zip, setZip] = useState("");
  const [noCoffee, setNoCoffee] = useState(false);
  const [saving, setSaving] = useState(false);

  const step = STEPS[stepIdx];
  const provider = store.aiProvider || "anthropic";
  const info = PROVIDERS[provider] || PROVIDERS.anthropic;
  const owned = new Set((store.appliances || []).map(a => a.library_id));

  const next = () => setStepIdx(i => Math.min(i + 1, STEPS.length - 1));
  const back = () => setStepIdx(i => Math.max(i - 1, 0));

  const savePrefs = (patch) =>
    store.setPreferences({ ...store.preferences, ...patch });
  const saveHousehold = (patch) =>
    savePrefs({ household: { ...(store.preferences.household || {}), ...patch } });

  // Store the answer AND the dial-in rules it implies. Without the second part
  // this question would change nothing, which is worse than not asking.
  // Editable afterwards in Settings; only ever seeded here.
  const saveTaste = (pref) =>
    savePrefs({
      coffee_taste_pref: pref,
      coffee_rules: coffeeRulesFor(pref, gearLine(store.gear)),
    });

  const finish = async () => {
    setSaving(true);
    try {
      if (zip.trim()) setStoredZip(zip.trim());
      await savePrefs({ onboarded_at: new Date().toISOString() });
      onFinish?.();
    } finally {
      setSaving(false);
    }
  };

  // Required steps refuse to advance; optional ones always can.
  const canAdvance =
    step === "provider" ? !!store.apiKey :
    step === "coffee" ? (noCoffee || owned.size > 0) :
    true;

  const field =
    "w-full rounded-[12px] px-3.5 py-2.5 bg-[var(--pg)] border border-[var(--pg-border)] " +
    "text-pronto-ink placeholder:text-pronto-text-3 text-[15px] focus:outline-none focus:border-pronto-navy";

  return (
    <div className="min-h-screen bg-pronto-page font-sf px-6 py-10 flex justify-center">
      <div className="w-full max-w-md">
        {/* Progress — shows how short this is, so it reads as finite. */}
        <div className="flex gap-1.5 mb-8">
          {STEPS.slice(0, -1).map((s, i) => (
            <div
              key={s}
              className={`h-1 flex-1 rounded-full ${i <= stepIdx ? "bg-pronto-accent" : "bg-[var(--pg-border)]"}`}
            />
          ))}
        </div>

        {step === "welcome" && (
          <>
            <h1 className="font-round text-2xl text-pronto-ink mb-2">Welcome to Pronto</h1>
            <p className="text-[15px] text-pronto-text-2 mb-6 leading-relaxed">
              Four short steps — about a minute. You can change any of it later in
              Settings, and skip anything that doesn't apply to you.
            </p>
            <label className="font-mono text-[10px] uppercase tracking-[0.16em] text-pronto-text-3 mb-1.5 block">
              What should we call you?
            </label>
            <input
              className={field}
              placeholder="e.g. Alex"
              defaultValue={store.preferences?.household?.display_name || ""}
              onBlur={(e) => saveHousehold({ display_name: e.target.value.trim() })}
            />
            <p className="text-[12px] text-pronto-text-3 mt-1.5">Optional — used when the assistant talks to you.</p>
          </>
        )}

        {step === "provider" && (
          <>
            <h1 className="font-round text-2xl text-pronto-ink mb-2">Connect an AI provider</h1>
            <p className="text-[15px] text-pronto-text-2 mb-5 leading-relaxed">
              Pronto uses your own API key, billed to your account. Photo scanning,
              recipe suggestions, and receipt parsing need this — it's the one step
              that isn't optional.
            </p>

            <div className="flex flex-wrap gap-2 mb-4">
              {Object.values(PROVIDERS).map(p => (
                <button
                  key={p.id}
                  onClick={() => store.setAiProvider(p.id)}
                  className={`rounded-[12px] px-3 py-2 text-[13px] font-semibold border ${
                    p.id === provider
                      ? "bg-pronto-accent text-white border-pronto-accent"
                      : "bg-[var(--pg)] text-pronto-ink border-[var(--pg-border)]"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {!providerSupports(provider, "web_tools") && (
              <div className="mb-4 rounded-[12px] px-3.5 py-3 bg-amber-50 border border-amber-200">
                <p className="text-[13px] text-amber-900 leading-relaxed">
                  Heads up: <strong>{info.label} can't fetch web pages</strong>, so
                  importing a recipe from a link and the chat assistant stay on
                  Anthropic. Everything else works normally.
                </p>
              </div>
            )}

            <input
              type="password"
              className={field}
              placeholder={info.keyPlaceholder}
              value={keyDraft}
              onChange={(e) => setKeyDraft(e.target.value)}
              onBlur={() => keyDraft.trim() && store.setApiKey(keyDraft.trim())}
            />
            {info.keyUrl && (
              <p className="text-[12px] text-pronto-text-3 mt-1.5">
                Get one at{" "}
                <a href={info.keyUrl} target="_blank" rel="noreferrer" className="text-pronto-navy underline">
                  {new URL(info.keyUrl).hostname}
                </a>
              </p>
            )}
          </>
        )}

        {step === "coffee" && (
          <>
            <h1 className="font-round text-2xl text-pronto-ink mb-2">Your coffee setup</h1>
            <p className="text-[15px] text-pronto-text-2 mb-5 leading-relaxed">
              This shapes every brew recommendation — dose, grind, and temperature
              are calculated for your specific gear.
            </p>

            <button
              onClick={() => setNoCoffee(v => !v)}
              className={`w-full mb-4 rounded-[12px] px-3.5 py-2.5 text-[14px] font-semibold border ${
                noCoffee
                  ? "bg-pronto-accent text-white border-pronto-accent"
                  : "bg-[var(--pg)] text-pronto-ink border-[var(--pg-border)]"
              }`}
            >
              I don't drink coffee — skip this
            </button>

            {!noCoffee && (
              <>
                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-pronto-text-3 mb-2">Brewer</p>
                <div className="flex flex-wrap gap-2 mb-5">
                  {BREWERS.map(a => (
                    <button
                      key={a.id}
                      onClick={() => store.toggleLibraryAppliance(a)}
                      className={`rounded-[12px] px-3 py-2 text-[13px] border ${
                        owned.has(a.id)
                          ? "bg-pronto-accent text-white border-pronto-accent"
                          : "bg-[var(--pg)] text-pronto-ink border-[var(--pg-border)]"
                      }`}
                    >
                      {a.icon} {a.name}
                    </button>
                  ))}
                </div>

                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-pronto-text-3 mb-2">Grinder</p>
                <div className="flex flex-wrap gap-2">
                  {GRINDERS.map(a => (
                    <button
                      key={a.id}
                      onClick={() => store.toggleLibraryAppliance(a)}
                      className={`rounded-[12px] px-3 py-2 text-[13px] border ${
                        owned.has(a.id)
                          ? "bg-pronto-accent text-white border-pronto-accent"
                          : "bg-[var(--pg)] text-pronto-ink border-[var(--pg-border)]"
                      }`}
                    >
                      {a.icon} {a.name}
                    </button>
                  ))}
                </div>
                <p className="text-[12px] text-pronto-text-3 mt-3">
                  Don't see your grinder? Pick the closest and refine it later in Appliances.
                </p>
              </>
            )}
          </>
        )}

        {step === "taste" && (
          <>
            <h1 className="font-round text-2xl text-pronto-ink mb-2">How do you take it?</h1>
            <p className="text-[15px] text-pronto-text-2 mb-5 leading-relaxed">
              Optional, and easy to change. This biases every brew suggestion.
            </p>
            {Object.values(TASTE_PROFILES).map(o => {
              const active = store.preferences?.coffee_taste_pref === o.id;
              return (
                <button
                  key={o.id}
                  onClick={() => saveTaste(o.id)}
                  className={`w-full text-left mb-2 rounded-[12px] px-3.5 py-3 border ${
                    active ? "bg-pronto-accent/10 border-pronto-accent" : "bg-[var(--pg)] border-[var(--pg-border)]"
                  }`}
                >
                  <div className="text-[15px] font-semibold text-pronto-ink">{o.label}</div>
                  <div className="text-[12px] text-pronto-text-2 mt-0.5">{o.hint}</div>
                </button>
              );
            })}
          </>
        )}

        {step === "home" && (
          <>
            <h1 className="font-round text-2xl text-pronto-ink mb-2">A couple of extras</h1>
            <p className="text-[15px] text-pronto-text-2 mb-5 leading-relaxed">
              All optional. Leave anything blank and Pronto simply won't show that step.
            </p>

            <label className="font-mono text-[10px] uppercase tracking-[0.16em] text-pronto-text-3 mb-1.5 block">
              ZIP code
            </label>
            <input
              className={field}
              placeholder="10001"
              value={zip}
              onChange={(e) => setZip(e.target.value)}
            />
            <p className="text-[12px] text-pronto-text-3 mt-1.5 mb-4">
              For weather-aware brew suggestions — iced when it's hot.
            </p>

            <label className="font-mono text-[10px] uppercase tracking-[0.16em] text-pronto-text-3 mb-1.5 block">
              Pet's name
            </label>
            <input
              className={field}
              placeholder="e.g. Mackenzie"
              defaultValue={store.preferences?.household?.pets?.[0]?.name || ""}
              onBlur={(e) => {
                const name = e.target.value.trim();
                saveHousehold({ pets: name ? [{ name, emoji: "🐾", meds_label: "medication" }] : [] });
              }}
            />
            <p className="text-[12px] text-pronto-text-3 mt-1.5">
              Adds a morning medication reminder. Skip if it doesn't apply.
            </p>
          </>
        )}

        {step === "done" && (
          <>
            <h1 className="font-round text-2xl text-pronto-ink mb-2">You're set</h1>
            <p className="text-[15px] text-pronto-text-2 mb-6 leading-relaxed">
              Start by adding a few pantry items or a recipe — snap a photo and
              Pronto will fill in the details. Everything you just set is in
              Settings if you want to change it.
            </p>
          </>
        )}

        {/* Nav */}
        <div className="flex items-center gap-3 mt-8">
          {stepIdx > 0 && (
            <button onClick={back} className="text-[14px] text-pronto-text-2 hover:text-pronto-ink">
              Back
            </button>
          )}
          <div className="flex-1" />
          {step === "done" ? (
            <button
              onClick={finish}
              disabled={saving}
              className="rounded-[12px] px-5 py-2.5 font-semibold text-[15px] bg-pronto-accent text-white disabled:opacity-40"
            >
              {saving ? "Saving…" : "Start cooking"}
            </button>
          ) : (
            <button
              onClick={next}
              disabled={!canAdvance}
              className="rounded-[12px] px-5 py-2.5 font-semibold text-[15px] bg-pronto-accent text-white disabled:opacity-40"
            >
              {canAdvance ? "Continue" : step === "provider" ? "Add a key to continue" : "Pick your gear"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
